import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { epochLabel } from '@/features/projects/format'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  loadRun,
  loadRuns,
  runSelected,
  selectMigrationNonce,
  selectMigrationRuns,
  selectRunsError,
  selectRunsLoading,
  selectSelectedRunId,
  toDisplayError
} from '../slices/migrationSlice'
import type { MigrationRunKind, MigrationRunStatus } from '../types'

const RUN_STATUS_TONE: Record<MigrationRunStatus, StatusTone> = {
  IN_PROGRESS: 'proposed',
  COMPLETED: 'done',
  ABORTED: 'blocked'
}

const RUN_KIND_TONE: Record<MigrationRunKind, StatusTone> = {
  REHEARSAL: 'neutral',
  LIVE: 'people'
}

/**
 * The runs panel (AC-1) — read-only (Decision 1: the three run-operations
 * writes are a separate deliverable). Self-contained: its own fetch, its own
 * loading/error/empty triad, keyed on the SELECTED plan.
 */
export function MigrationRunsPanel({
  orgId,
  projectId,
  planId
}: {
  orgId: string
  projectId: string
  planId: string | null
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const runs = useAppSelector(selectMigrationRuns)
  const selectedRunId = useAppSelector(selectSelectedRunId)
  const loading = useAppSelector(selectRunsLoading)
  const error = useAppSelector(selectRunsError)
  const nonce = useAppSelector(selectMigrationNonce)

  useEffect(() => {
    if (planId === null) return
    void dispatch(loadRuns({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId, nonce])

  const onRetryClicked = useCallback(() => {
    if (planId === null) return
    void dispatch(loadRuns({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId])

  const onRunRowClicked = useCallback(
    (runId: string) => {
      dispatch(runSelected(runId))
      if (planId !== null) void dispatch(loadRun({ orgId, projectId, planId, runId }))
    },
    [dispatch, orgId, projectId, planId]
  )

  if (planId === null) {
    return (
      <section aria-label={t('migration.runs.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
        <SectionLabel>{t('migration.runs.title')}</SectionLabel>
        <div className="mt-2">
          <EmptyPanel title={t('migration.runs.noPlanTitle')} body={t('migration.runs.noPlanBody')} />
        </div>
      </section>
    )
  }

  return (
    <section
      aria-label={t('migration.runs.title')}
      className="rounded-lg border border-border bg-card px-4 py-3.5"
      data-testid="migration-runs-panel"
    >
      <SectionLabel>{t('migration.runs.title')}</SectionLabel>

      {error !== null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(error)} onRetryClicked={onRetryClicked} />
        </div>
      ) : loading && runs.length === 0 ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.runs.loading')} />
        </div>
      ) : runs.length === 0 ? (
        <div className="mt-2">
          <EmptyPanel title={t('migration.runs.emptyTitle')} body={t('migration.runs.emptyBody')} />
        </div>
      ) : (
        <ul className="mt-2 overflow-hidden rounded-lg border border-border">
          {runs.map((run) => (
            <li key={run.id}>
              <button
                type="button"
                onClick={() => onRunRowClicked(run.id)}
                aria-pressed={run.id === selectedRunId}
                data-testid={`migration-run-row-${run.id}`}
                className={`v-row flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left ${
                  run.id === selectedRunId ? 'bg-accent' : 'hover:bg-muted'
                }`}
              >
                <StatusPill tone={RUN_KIND_TONE[run.kind]}>{t(`migration.runKind.${run.kind}`)}</StatusPill>
                <StatusPill tone={RUN_STATUS_TONE[run.status]}>{t(`migration.runStatus.${run.status}`)}</StatusPill>
                <span className="text-xs text-muted-foreground">
                  {t('migration.runs.startedBy', { name: run.startedByUserName, at: epochLabel(run.startedAt) })}
                </span>
                {run.finishedByUserName !== undefined && (
                  <span className="text-xs text-muted-foreground">
                    {t('migration.runs.finishedBy', {
                      name: run.finishedByUserName,
                      at: epochLabel(run.finishedAt)
                    })}
                  </span>
                )}
                <span className="ml-auto text-xs text-muted-foreground">
                  {t('migration.runs.objectCount', { count: run.counts.length })}
                </span>
              </button>
              {run.counts.length > 0 && run.id === selectedRunId && (
                <ul className="border-t border-border bg-muted/30 px-6 py-2">
                  {run.counts.map((count) => (
                    <li key={count.objectKey} className="v-row flex flex-wrap gap-x-3 py-1 text-xs text-muted-foreground">
                      <span className="font-mono text-foreground">{count.objectKey}</span>
                      <span>
                        {t('migration.runs.counts', {
                          source: count.sourceCount ?? '—',
                          target: count.targetCount ?? '—',
                          moved: count.movedCount ?? '—'
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
